"use client";

/**
 * Deterministic scroll-to-top on route PUSH navigations, site-wide.
 *
 * Next's built-in scroll reset proved unreliable here (arriving on
 * /calculate from a scrolled homepage kept the old offset - reported
 * across pages), so this watches the pathname and pins the viewport
 * to the top explicitly. Two deliberate exceptions:
 *   - popstate (back/forward): the browser's own scroll restoration
 *     is correct there - skip exactly one reset after it fires;
 *   - hash links: the anchor is the scroll target, leave it alone;
 *   - a language switch (/az/x -> /ru/x): same page, so the reader
 *     stays where they were (it used to jump to the top, owner
 *     2026-10-07).
 * Renders nothing; mounted once in the [locale] layout.
 */
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

const withoutLocale = (p: string) => p.replace(/^\/(az|ru|en)(?=\/|$)/, "") || "/";

export default function ScrollReset() {
  const pathname = usePathname();
  const popped = useRef(false);
  const first = useRef(true);
  const prev = useRef(pathname);

  useEffect(() => {
    const onPop = () => {
      popped.current = true;
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    const last = prev.current;
    prev.current = pathname;
    if (first.current) {
      first.current = false;
      return;
    }
    if (withoutLocale(last) === withoutLocale(pathname)) return;
    if (popped.current) {
      popped.current = false;
      return;
    }
    if (window.location.hash) return;
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
